/**
 * Counts reps as full revolutions of the upper arm.
 *
 * A standing arm swing circles: the arm goes down, forward, overhead, behind
 * and down again, so the upper-arm vector genuinely revolves rather than
 * oscillating. On a ten-rep recording the signed arm angle relative to the
 * trunk accumulates -3600 degrees — 10.00 revolutions against ten performed.
 *
 * This is why the counter reads the SIGNED angle and accumulates it, while the
 * amplitude metrics read `elevation` and must not. Elevation is bounded 0-180
 * by construction, which is what makes it right for a windowed peak-to-peak and
 * useless here: a full circle and a raise-and-lower both trace 0 to 180 and
 * back, and only the accumulated signal tells them apart. Accumulating is wrong
 * for amplitude and necessary for counting; see `armElevation()` for the other
 * half of that split.
 */

/** Smallest signed step between two angles, resolving the wrap at +/-180. */
function step(from, to) {
  let delta = to - from;
  while (delta > 180) {
    delta -= 360;
  }
  while (delta < -180) {
    delta += 360;
  }
  return delta;
}

export class RepCounter {
  /**
   * @param options.minVisibility Frames whose arm or trunk landmarks fall below
   *   this are refused outright. Without a gate, a take where the athlete walks
   *   into and out of shot counts 11 reps instead of 10, because the walks carry
   *   real arm movement the model is partly guessing at.
   *
   *   The gate reads arm and trunk, NOT legs: counting is a question about the
   *   arm, so a take framed from the knees up still counts — it simply cannot be
   *   scored for tiptoes or knee flexion. The legs are also the less reliable
   *   half of the pose. On the reference recordings 99 to 106 frames fall below
   *   this threshold on the legs against 83 to 88 on the arm and trunk, so a leg
   *   gate would break accumulation on frames where the arm is perfectly clear.
   * @param options.maxGapMs A longer gap between frames breaks the
   *   accumulation. Real recordings drop frames — one 490ms gap in 29 seconds,
   *   spanning about a quarter of a rep — and bridging one invents travel the
   *   arm may not have made.
   */
  constructor({ minVisibility = 0.5, maxGapMs = 250 } = {}) {
    this.minVisibility = minVisibility;
    this.maxGapMs = maxGapMs;
    this.reset();
  }

  reset() {
    this.count = 0;
    this.travel = 0;
    this.last = null;
    /** Samples accumulated since the current rep began. */
    this.current = [];
    /** The most recently completed rep's samples, for scoring. */
    this.lastRep = null;
  }

  /**
   * Feeds one sample in. Returns the completed rep's samples when this sample
   * closed a revolution, otherwise null.
   *
   * @param sample From `buildSample()`.
   * @param side Which arm to follow, from `nearSide()`.
   */
  push(sample, side) {
    if (sample.swingVisibility[side] < this.minVisibility) {
      // Not a failed rep, just an unusable frame. Drop the partial rep rather
      // than resuming across the gap with a stale angle.
      this.break();
      return null;
    }

    const angle = sample.armAngle[side];
    if (this.last == null || sample.t - this.last.t > this.maxGapMs) {
      this.last = { t: sample.t, angle };
      this.current = [sample];
      return null;
    }

    this.travel += step(this.last.angle, angle);
    this.last = { t: sample.t, angle };
    this.current.push(sample);

    if (Math.abs(this.travel) < 360) {
      return null;
    }

    // One revolution closed. Carry the overshoot rather than discarding it, so
    // a slow drift in frame timing cannot shed a rep over a long set.
    this.travel -= Math.sign(this.travel) * 360;
    this.count += 1;
    this.lastRep = this.current;
    this.current = [sample];
    return this.lastRep;
  }

  /** Abandons the rep in progress, keeping the count so far. */
  break() {
    this.last = null;
    this.travel = 0;
    this.current = [];
  }

  /** How far through the current rep, 0 to 1. For a progress readout. */
  get progress() {
    return Math.abs(this.travel) / 360;
  }
}
